import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowLeft, KeyRound, Check, Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';
import API from '@/lib/api';

const PASSWORD_RULES = [
  { key: 'len', label: 'At least 8 characters', test: (v) => v.length >= 8 },
  { key: 'upper', label: 'One uppercase letter', test: (v) => /[A-Z]/.test(v) },
  { key: 'lower', label: 'One lowercase letter', test: (v) => /[a-z]/.test(v) },
  { key: 'num', label: 'One number', test: (v) => /[0-9]/.test(v) },
  { key: 'sym', label: 'One special character', test: (v) => /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\/'`~]/.test(v) },
];

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ email: '', code: '', new_password: '', confirm: '' });

  const passOK = PASSWORD_RULES.every((r) => r.test(form.new_password));
  const matchOK = form.new_password && form.new_password === form.confirm;

  const requestCode = async (e) => {
    e?.preventDefault();
    if (!form.email.includes('@')) { toast.error('Enter a valid email address'); return; }
    setLoading(true);
    try {
      const res = await API.post('/auth/forgot-password', { email: form.email });
      if (res.data.reset_code_mock) setForm((f) => ({ ...f, code: res.data.reset_code_mock }));
      toast.success("If that email exists, we've sent a 6-digit code.");
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Try again.');
    } finally { setLoading(false); }
  };

  const resetPassword = async (e) => {
    e?.preventDefault();
    if (form.code.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    if (!passOK) { toast.error('Password does not meet the requirements'); return; }
    if (!matchOK) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await API.post('/auth/reset-password', {
        email: form.email, code: form.code, new_password: form.new_password,
      });
      toast.success('Password reset — please sign in.');
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reset password.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F6F8FC] flex items-center justify-center px-4 py-12" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/" className="font-heading text-2xl font-semibold text-[#101828]">Mylikeness</Link>
        </div>
        <div className="bg-white rounded-2xl border border-[#E4E7EC] p-7 shadow-sm">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.form key="step1" onSubmit={requestCode}
                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                className="space-y-5">
                <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center">
                  <KeyRound className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-[#101828]">Reset your password</h1>
                  <p className="text-[#475467] text-sm mt-1">Enter your account email and we'll send you a 6-digit reset code.</p>
                </div>
                <div>
                  <Label className="text-[#101828] text-sm font-medium mb-1.5 block">Email</Label>
                  <Input data-testid="forgot-email-input" type="email" placeholder="you@example.com"
                    value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-white border-[#D0D5DD] h-11" required />
                </div>
                <Button data-testid="forgot-submit-email-btn" type="submit" disabled={loading}
                  className="w-full btn-primary h-11 text-sm font-medium">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : 'Send reset code'}
                </Button>
                <Link to="/login" data-testid="forgot-back-to-login"
                  className="flex items-center justify-center gap-1.5 text-sm text-[#475467] hover:text-[#101828]">
                  <ArrowLeft className="w-4 h-4" /> Back to sign in
                </Link>
              </motion.form>
            )}

            {step === 2 && (
              <motion.form key="step2" onSubmit={resetPassword}
                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                className="space-y-4">
                <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-[#101828]">Enter code & new password</h1>
                  <p className="text-[#475467] text-sm mt-1">
                    We sent a 6-digit code to <strong className="text-[#101828]">{form.email}</strong>. It expires in 15 minutes.
                  </p>
                </div>
                <div>
                  <Label className="text-[#101828] text-sm font-medium mb-1.5 block">6-digit code</Label>
                  <Input data-testid="reset-code-input" inputMode="numeric" maxLength={6} placeholder="000000"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                    className="bg-white border-[#D0D5DD] h-11 tracking-[0.4em] text-center font-mono text-lg" required />
                </div>
                <div>
                  <Label className="text-[#101828] text-sm font-medium mb-1.5 block">New password</Label>
                  <div className="relative">
                    <Input data-testid="reset-new-password-input" type={showPw ? 'text' : 'password'}
                      placeholder="Enter new password" value={form.new_password}
                      onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                      className="bg-white border-[#D0D5DD] h-11 pr-10" required />
                    <button type="button" onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#475467] hover:text-[#101828]">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {form.new_password && (
                    <div className="mt-2 space-y-1">
                      {PASSWORD_RULES.map((r) => {
                        const ok = r.test(form.new_password);
                        return (
                          <div key={r.key} className="flex items-center gap-1.5 text-[11px]">
                            <div className={`w-3 h-3 rounded-full flex items-center justify-center ${ok ? 'bg-emerald-500 text-white' : 'bg-slate-200'}`}>
                              {ok && <Check className="w-2 h-2" />}
                            </div>
                            <span className={ok ? 'text-emerald-700' : 'text-slate-400'}>{r.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-[#101828] text-sm font-medium mb-1.5 block">Confirm new password</Label>
                  <Input data-testid="reset-confirm-input" type="password" placeholder="Re-enter new password"
                    value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                    className="bg-white border-[#D0D5DD] h-11" required />
                  {form.confirm && !matchOK && (
                    <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> Passwords don't match
                    </p>
                  )}
                </div>
                <Button data-testid="reset-submit-btn" type="submit" disabled={loading || !passOK || !matchOK || form.code.length !== 6}
                  className="w-full btn-primary h-11 text-sm font-medium">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Resetting…</> : 'Reset password'}
                </Button>
                <div className="flex items-center justify-between text-sm">
                  <button type="button" data-testid="reset-resend-btn" onClick={requestCode} disabled={loading}
                    className="text-blue-600 hover:text-blue-700 hover:underline">Resend code</button>
                  <button type="button" onClick={() => setStep(1)}
                    className="text-[#475467] hover:text-[#101828]">Change email</button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
